import type { Ssm5Session } from '@packages/domain/ssm5';

import { DetailView, DetailViewBody } from '@web/components/detail-view';
import { useNavigationGuard } from '@web/core/navigation/use-navigation-guard';
import NoMatch from '@web/views/no-match';
import { useVinSessionState } from '@web/vin-sessions/context/vin-sessions.hooks';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import type { Ssm5ConnectionState, Ssm5VehicleMatch } from '../context/ssm5-connection.context';
import type { Ssm5FreezeFramesTarget } from '../helpers/ssm5-paths';

import { ApplicableModelsModal } from '../components/applicable-models-modal';
import { Ssm5ConnectColumns } from '../components/ssm5-connect-columns';
import { Ssm5ConnectingPanel } from '../components/ssm5-connecting-panel';
import { Ssm5ConnectionLostModal } from '../components/ssm5-connection-lost-modal';
import { Ssm5LeaveWarningModal } from '../components/ssm5-leave-warning-modal';
import { Ssm5PageHeader } from '../components/ssm5-page-header';
import { Ssm5SessionBody } from '../components/ssm5-session-body';
import { Ssm5SlowConnectionModal } from '../components/ssm5-slow-connection-modal';
import { Ssm5VehicleMatchModal } from '../components/ssm5-vehicle-match-modal';
import { isSsm5Path, toSsm5FreezeFramesRoute } from '../helpers/ssm5-paths';
import { useSsm5Connection } from '../hooks/use-ssm5-connection';

const useStyles = createUseStyles({
    body: {
        display: 'flex',
        gap: 24,
        padding: 24,
    },
    // The session body scrolls inside its panel, so the page itself never grows past the view.
    sessionBody: {
        display: 'flex',
        minHeight: 0,
        overflow: 'hidden',
        padding: 0,
    },
});

/**
 * The SSM5 Diagnostic page.
 *
 * @returns the page
 */
export function Ssm5Diagnostics() {
    const classes = useStyles();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { selectedVin } = useVinSessionState();
    const isInVinSession = selectedVin !== undefined;
    const [isModelsModalOpen, setIsModelsModalOpen] = useState(false);
    const [stayedSession, setStayedSession] = useState<Ssm5Session>();
    const { cancel, connect, continueToVinSession, keepWaiting, restart, restartAttempt, state } = useSsm5Connection();
    const isConnecting = state.status === 'connecting';
    const isViewingSession = state.status === 'connected' || state.status === 'disconnected';
    const viewedSessionVin = isViewingSession ? state.session.vin : undefined;
    const { redirectPath, target } = toSsm5FreezeFramesRoute(location, viewedSessionVin);
    const isUnknownAddress = !isSsm5Path(location.pathname);
    const isFreezeFramesOpen = target !== undefined;
    const shouldRedirect = redirectPath !== undefined;
    const isTakingLong = state.status === 'connecting' && state.isTakingLong;
    const lostSession = state.status === 'disconnected' ? state.session : undefined;
    const shouldHold = useCallback((to: string) => isConnecting && !isSsm5Path(to), [isConnecting]);
    const { heldPath, proceed, stay } = useNavigationGuard(shouldHold);
    // Only the top modal takes Escape and focus, so the notice waits while the leave warning asks.
    const isSlowConnectionModalOpen = isTakingLong && heldPath === undefined;

    useEffect(() => cancel, [cancel]);

    const openModelsModal = useCallback(() => setIsModelsModalOpen(true), []);
    const stayDisconnected = useCallback(() => setStayedSession(lostSession), [lostSession]);
    const closeModelsModal = useCallback(() => setIsModelsModalOpen(false), []);
    const openVinSession = useCallback(
        (match: Ssm5VehicleMatch) => {
            navigate(`/vin/${match.session.vin}/ssm5`);
            continueToVinSession();
        },
        [continueToVinSession, navigate]
    );

    if (isUnknownAddress) {
        return <NoMatch />;
    }

    return (
        <DetailView>
            {shouldRedirect && <Navigate replace to={redirectPath} />}
            {!isFreezeFramesOpen && (
                <Ssm5PageHeader
                    actionLabel={t('ssm5:applicableModels.link')}
                    onActionPress={openModelsModal}
                    title={t('ssm5:title')}
                />
            )}
            <DetailViewBody className={isViewingSession ? classes.sessionBody : classes.body}>
                {connectFlowBody(state, connect, restart, isInVinSession, target)}
            </DetailViewBody>
            <ApplicableModelsModal onHide={closeModelsModal} show={isModelsModalOpen} />
            <Ssm5VehicleMatchModal
                match={state.status === 'vehicleMatched' ? state : undefined}
                onCancel={cancel}
                onContinue={openVinSession}
                sessionVin={selectedVin?.vinRecord.vin}
            />
            <Ssm5LeaveWarningModal onLeave={proceed} onStay={stay} show={heldPath !== undefined} />
            <Ssm5SlowConnectionModal
                onKeepWaiting={keepWaiting}
                onRestart={restartAttempt}
                show={isSlowConnectionModalOpen}
            />
            <Ssm5ConnectionLostModal
                onRestart={restart}
                onStay={stayDisconnected}
                show={lostSession !== undefined && lostSession !== stayedSession}
            />
        </DetailView>
    );
}

/**
 * Pick the page body for the connection state.
 *
 * @param state - the connection state
 * @param onConnect - receives the normalized Cloud Connector ID
 * @param onReconnect - leaves the session behind for the connect flow, when its DTCs cannot be read
 * @param isInVinSession - whether the page runs inside an open VIN session
 * @param freezeFramesTarget - the DTC whose FFD page is open, if any
 * @returns the body content
 */
function connectFlowBody(
    state: Ssm5ConnectionState,
    onConnect: (cloudConnectorId: string) => void,
    onReconnect: () => void,
    isInVinSession: boolean,
    freezeFramesTarget?: Ssm5FreezeFramesTarget
) {
    switch (state.status) {
        case 'connected':
            return (
                <Ssm5SessionBody
                    freezeFramesTarget={freezeFramesTarget}
                    isConnected
                    onReconnect={onReconnect}
                    session={state.session}
                />
            );
        case 'disconnected':
            return (
                <Ssm5SessionBody
                    freezeFramesTarget={freezeFramesTarget}
                    isConnected={false}
                    onReconnect={onReconnect}
                    session={state.session}
                />
            );
        case 'connecting':
            return (
                <Ssm5ConnectingPanel isInVinSession={isInVinSession} phase={state.phase} progress={state.progress} />
            );
        case 'vehicleMatched':
            return <Ssm5ConnectingPanel isInVinSession={isInVinSession} phase="matchingVehicle" progress={0} />;
        case 'failed':
            return <Ssm5ConnectColumns failure={state} onConnect={onConnect} />;
        case 'idle':
            return <Ssm5ConnectColumns onConnect={onConnect} />;
    }
}
